// A section of one record type: saved view → working copy → live query → table or board; a record opens as its own page (R-U6).

import { newRecordData } from '../record/new-record.ts';
import { Gantt } from '../gantt/Gantt.tsx';
import { Calendar } from '../calendar/Calendar.tsx';
import { tableGroups } from '../table/groups.ts';
import { Summary } from '../summary/Summary.tsx';
import { useEffect, useMemo, useState } from 'react';
import { canImport, canRead, canWrite, hasRight, recordTitle, textKey, type Id, accountToday } from '@confdeck/core-shared';
import { HERE_LIMIT, useScreenSearch, type ScreenHit } from './screen-search.ts';
import { Board } from '../board/Board.tsx';
import { Cards } from '../board/Cards.tsx';
import { createRecord, freshRecords } from '../sync/mutations.ts';
import { ArchiveConfirm } from '../record/ArchiveConfirm.tsx';
import { useAccess, useDebounced, useMeta, useQuery, useStatus, useSync } from '../sync/react.tsx';
import { DataTable, plural } from '../table/DataTable.tsx';
import { FieldSettings } from '../table/FieldSettings.tsx';
import { ModalStack } from '../ui/ModalStack.tsx';
import { Toolbar } from '../table/Toolbar.tsx';
import { useViewState } from '../table/view-state.ts';
import { offeredViews } from '../table/view-access.ts';
import { scopeField, useScope, withScope } from './scope.tsx';
import { listHref, narrowList, termField } from './nav-terms.ts';
import { menuViewIds, switchViews } from './nav-tree.ts';
import { useLocation } from './router.ts';
import { branchFilter, pipelineField, pipelineSectionOf } from '../pipelines/pipeline-section.ts';
import { BulkEdit } from '../table/BulkEdit.tsx';
import { typeEmptyOf, typeNoticesOf } from './screens.ts';
import { ImportDialog } from '../table/ImportDialog.tsx';
import { downloadExport } from '../table/export.ts';
import { Button, useToast } from '../ui/primitives.tsx';
import { navigate, recordHref, typeHref, wantsNewTab } from './router.ts';
import { rememberList } from './nav-place.ts';
import s from './shell.module.css';
import { BulkMail } from '../mail/BulkMail.tsx';
import { askedFields, NewRecordDialog } from '../record/NewRecordDialog.tsx';

export function TypeScreen({ typeKey, viewId, scoped }: { typeKey: string; viewId: string | null; scoped: boolean }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const access = useAccess();
  const { state } = useStatus();
  const type = meta.typeByKey(typeKey);
  // A section that lives in pipelines (R-U29): «Все» is only a list — the board of a pipeline is the pipeline's own page (R-U31).
  const piped = pipelineSectionOf(type?.key);
  // Shared views and the person's own personal ones; others' personal views are theirs (R-U2).
  const views = type ? offeredViews(meta, access, client.header?.userId, type.id).filter((v) => !(piped && v.layout === 'board')) : [];
  // Views that are menu items of their own: the view button does not mix them with the other lists of the type, and the type's own
  // item does not open on one of them.
  const itemViews = new Set(client.menu.flatMap((m) => [...menuViewIds(m)]));
  const saved = views.find((v) => v.id === viewId) ?? views.find((v) => !itemViews.has(v.id)) ?? views[0];
  const vs = useViewState(saved);
  const [searchText, setSearch] = useState('');
  const search = useDebounced(searchText, 80);
  const [settings, setSettings] = useState<false | 'open' | 'add'>(false);
  const [selected, setSelected] = useState<Set<Id>>(new Set());
  const [bulk, setBulk] = useState<Id[] | null>(null);
  const [importing, setImporting] = useState(false);
  const [archiving, setArchiving] = useState<Id[] | null>(null);
  const [mailing, setMailing] = useState<{ ids: Id[]; label: string } | null>(null);
  const [asking, setAsking] = useState(false);
  const view = vs.view;

  useEffect(() => { setSelected(new Set()); setSearch(''); }, [type?.id, saved?.id]);
  // The screen stays mounted from one section of records to another: what was open over it closes with the move (um/2026-10-31/01 п.8).
  // Another view of the same section is another screen too (review P3-4).
  useEffect(() => { setSettings(false); setBulk(null); setMailing(null); setAsking(false); }, [typeKey, viewId]);

  const [scopeId] = useScope();
  const sField = scoped && type ? scopeField(meta, type.id, client.header?.scope?.typeId) : undefined;
  // A value chosen in the left column narrows every view of the section (um/2026-10-30/03 п.7).
  const where = useLocation().search;
  const termId = where.get('term');
  const tField = type ? termField(meta, type.id, termId) : undefined;
  // A branch of «Все» (R-U29: «Просроченные», «Без исполнителя», the stages by kind) narrows the list the same way.
  const branch = piped?.branches?.find((b) => b.key === where.get('branch'));
  const bField = branch && type ? pipelineField(meta, type.id) : undefined;
  const today = accountToday(meta);
  const branchF = useMemo(() => (piped && branch && bField ? branchFilter(meta, piped, bField, branch.kind, today) : null), [piped, branch, bField, today, meta, meta.all.size]);
  const filter = useMemo(() => narrowList(withScope(view?.filter ?? null, sField, scopeId), { field: tField, id: termId }, branchF), [view?.filter, sField, scopeId, tField, termId, branchF]);
  const update = vs.update;
  const q = useQuery(type?.id ?? '', filter, view?.sort ?? [], search);

  // Keep the URL view id stable so a link to a view stays a link to it.
  useEffect(() => { if (type && saved && viewId !== saved.id) navigate(listHref(type.key, saved.id, termId, branch?.key), { replace: true }); }, [type, saved, viewId]);
  // The crumb of a record page and its «назад» after a direct visit lead to the view the record was opened from.
  useEffect(() => { if (type && saved) rememberList(type.id, listHref(type.key, saved.id, tField ? termId : null, branch?.key)); }, [type, saved, tField, termId, branch?.key]);

  const open = (id: Id, e?: React.MouseEvent | React.KeyboardEvent) => {
    if (wantsNewTab(e as React.MouseEvent)) { window.open(recordHref(id), '_blank'); return; }
    navigate(recordHref(id));
  };

  // The global search looks here first (R-U23): the rows of the open view — its filter, the event, the value of the column — by title.
  // The search of the filter line stays the view's own filter.
  useScreenSearch(type ? (query) => {
    const k = textKey(query);
    const hits: ScreenHit[] = [];
    for (const id of q.ids) {
      const r = client.store.record(id);
      if (!r) continue;
      const title = recordTitle(r, meta);
      if (!textKey(title).includes(k)) continue;
      hits.push({ id, recordId: id, label: title, hint: type.nameMany, icon: type.icon, run: () => navigate(recordHref(id)) });
      if (hits.length >= HERE_LIMIT) break;
    }
    return hits;
  } : null);

  const columns = useMemo(() => view?.columns ?? [], [view?.columns]);
  const notices = typeNoticesOf(type?.key);
  const Empty = typeEmptyOf(type?.key);
  if (!type || !view || !saved) return <div className={s.empty}>Раздел не найден</div>;
  // A section the person's roles do not read says so, not «пока пусто» (Grok audit, round 2). While the connection is still coming up
  // the rights may not have arrived yet: the empty rows of the table show then, not a refusal (recheck N4).
  if (!canRead(access, type.id) && state === 'connecting') return <div className={s.content} aria-busy="true">{Array.from({ length: 12 }, (_, i) => <div key={i} className={s.skelRow} style={{ animationDelay: `${i * 40}ms` }} />)}</div>;
  if (!canRead(access, type.id)) return <div className={s.empty}>Нет доступа к разделу «{type.nameMany}». Его открывает администратор, выдав роль с этим разделом.</div>;

  // J6: a section with an email field can write to its rows, for those with the right «Рассылать письма по выборке».
  const mailable = meta.fields(type.id).some((f) => f.fieldType === 'email' && !f.archivedAt) && hasRight(access, 'mail.bulk');

  // Required fields besides the title are asked first: a record without them would be refused by the server and vanish (J7).
  const asked = askedFields(meta, type.id);
  const create = (extra: Record<string, unknown> = {}) => {
    const data = { ...newRecordData(meta, type.id, 'Без названия', { scope: sField && scopeId ? { field: sField.key, id: scopeId } : null, pipelineId: view.groupBy?.pipelineId }), ...extra };
    const id = createRecord(client, type.id, data);
    // Only a blank «Без названия» record is fresh: one made from the dialog already has what the person typed.
    if (!Object.keys(extra).length) freshRecords.add(id);
    setAsking(false);
    open(id);
  };

  return (
    <div className={s.typeScreen}>
      <Toolbar typeId={type.id} views={switchViews(views, itemViews, saved?.id)} view={view} onView={(id) => navigate(listHref(type.key, id, tField ? termId : null, branch?.key))} update={update}
        dirty={vs.dirty} canSave={vs.canSave} onSave={vs.save} onReset={vs.reset} actions={{ create: (name, from) => (from ? vs.create(name, from) : vs.saveAs(name)), patch: vs.patch, remove: vs.remove, mayEdit: vs.mayEdit, isOwner: vs.isOwner }}
        search={searchText} onSearch={setSearch} onSettings={() => setSettings(settings ? false : 'open')} onCreate={meta.field(type.id, type.titleField)?.options.managed || !canWrite(access, type.id) ? undefined : () => (asked.length ? setAsking(true) : create())} shown={q.ids.length}
        onImport={canImport(access, type.id) ? () => setImporting(true) : undefined}
        onExport={async () => {
          try {
            const file = await downloadExport(type.id, saved?.id ?? null, scopeId, { filter: narrowList(view.filter, { field: tField, id: termId }, branchF), search });
            toast({ kind: 'info', text: `Файл ${file.name}: записей — ${file.rows}` });
          } catch (e) {
            toast({ kind: 'error', text: (e as Error).message });
          }
        }}
        drill={vs.drilled ? { chips: vs.drilled.chips, onBack: vs.back } : undefined}
        onMail={mailable ? { label: selected.size ? `Письмо выбранным (${selected.size})` : `Письмо всем в выборке (${q.ids.length})`, run: () => setMailing(selected.size ? { ids: [...selected], label: `выбранные — ${selected.size}` } : { ids: [...q.ids], label: `вся выборка — ${q.ids.length}` }) } : undefined} />
      {asking && <NewRecordDialog typeId={type.id} fields={asked} onCreate={create} onClose={() => setAsking(false)} />}
      {mailing && <BulkMail typeId={type.id} ids={mailing.ids} label={mailing.label} onClose={() => setMailing(null)} />}
      <ImportDialog typeId={type.id} open={importing} onClose={() => setImporting(false)} />
      {archiving && <ArchiveConfirm ids={archiving} title={`${archiving.length} ${plural(archiving.length, 'запись', 'записи', 'записей')}`} onClose={() => setArchiving(null)} onDone={() => setSelected(new Set())} />}
      <div className={s.typeBody}>
        <div className={s.listArea}>
          {notices.map((n) => <n.component key={n.key} />)}
          {q.ids.length === 0 && (search || view.filter || tField || branch) ? (
            <div className={s.empty}>
              <div>{tField && !search && !view.filter ? `В «${type.nameMany}» нет записей со значением «${meta.term(termId as Id)?.name ?? ''}».`
                : branch && !search && !view.filter ? `В «${piped?.words.all ?? type.nameMany}» нет записей ветки «${branch.label}».` : 'Ничего не нашлось. Измените поиск или фильтры.'}</div>
              <span className={s.emptyActions}>
                {/* The value chosen in the column is a filter too: it is taken off here, not only in the column. */}
                {tField && <Button variant="secondary" onClick={() => navigate(listHref(type.key, saved.id, null))}>Снять фильтр «{meta.term(termId as Id)?.name ?? ''}»</Button>}
                {(search || view.filter) && <Button variant="secondary" onClick={() => { setSearch(''); vs.update({ filter: null }); }}>Сбросить поиск и фильтры</Button>}
              </span>
            </div>
          ) : q.ids.length === 0 && Empty && canWrite(access, type.id) ? (
            <Empty />
          ) : q.ids.length === 0 ? (
            <div className={s.empty}>
              <div>В разделе «{type.nameMany}» пока пусто.</div>
              {!meta.field(type.id, type.titleField)?.options.managed && canWrite(access, type.id) && <Button variant="primary" icon="plus" onClick={() => (asked.length ? setAsking(true) : create())}>{type.name}</Button>}
            </div>
          ) : view.layout === 'summary' ? (
            <Summary typeId={type.id} view={view} ids={q.ids} onAxes={(summary) => vs.update({ summary })} onCell={(filter, chips) => vs.drill({ layout: 'table', filter }, chips)} />
          ) : view.layout === 'gantt' ? (
            <Gantt typeId={type.id} view={view} ids={q.ids} onOpen={open} openId={null} />
          ) : view.layout === 'calendar' ? (
            <Calendar typeId={type.id} view={view} ids={q.ids} onOpen={open} />
          ) : view.layout === 'cards' ? (
            <Cards typeId={type.id} view={view} ids={q.ids} onOpen={open} />
          ) : view.layout === 'board' ? (
            <Board typeId={type.id} view={view} ids={q.ids} onOpen={open} openId={null} />
          ) : (
            <DataTable typeId={type.id} ids={q.ids} groups={tableGroups(meta, (id) => client.store.record(id), type.id, view.groupBy, q.ids)} columns={columns} onColumns={(c) => vs.update({ columns: c })}
              sortState={view.sort} onSort={(field, dir) => vs.update({ sort: dir ? [{ field, dir }] : [] })}
              onFilterBy={(field) => { vs.update({ filter: { and: [...((view.filter && 'and' in view.filter) ? view.filter.and : []), { field, op: meta.field(type.id, field)?.fieldType === 'relation' ? 'some' : 'contains' }] } }); }}
              onOpen={open} onAddField={() => setSettings('add')} openId={null} selected={selected} onSelect={setSelected} />
          )}
          {selected.size > 0 && (
            <div className={s.bulk} role="toolbar" aria-label="Действия с выбранными">
              <span>Выбрано {selected.size} {plural(selected.size, 'запись', 'записи', 'записей')}</span>
              {canWrite(access, type.id) && <Button size="sm" icon="edit" onClick={() => setBulk([...selected])}>Изменить поле</Button>}
              <Button size="sm" icon="archive" onClick={() => setArchiving([...selected])}>В архив</Button>
              <Button size="sm" icon="close" onClick={() => setSelected(new Set())}>Снять выбор</Button>
            </div>
          )}
          {bulk && <BulkEdit typeId={type.id} ids={bulk} onClose={() => { setBulk(null); setSelected(new Set()); }} />}
          {/* The field settings are a modal of the core's stack (R-U19, um/2026-10-31/01 п.7), not a panel stuck to the edge. */}
          <ModalStack onClose={() => setSettings(false)} layers={settings ? [{ key: 'fields', label: `Поля · ${type.nameMany}`, size: 'panel',
            content: <FieldSettings typeId={type.id} columns={columns} onColumns={(c) => vs.update({ columns: c })} onClose={() => setSettings(false)} startAdding={settings === 'add'} /> }] : []} />
        </div>
      </div>
    </div>
  );
}
